// Wohler Chemical — Dissociatives & NMDA Antagonists (Program F)

export const dissociativeCompounds = {
  "deschloroketamine": {
    id: "deschloroketamine",
    name: "Deschloroketamine (DCK)",
    fullName: "2-(methylamino)-2-phenylcyclohexan-1-one",
    aliases: ["DCK", "2'-Oxo-PCM"],
    categoryId: "dissociatives",
    program: "Program F",
    cas: "4551-92-2",
    formula: "C₁₃H₁₇NO",
    molecularWeight: "203.28 g/mol",
    schedulingStatus: "unscheduled",
    schedulingNote: "Not federally scheduled as of 2026. 2-Fluorodeschloroketamine (2-FDCK) was federally scheduled January 2026 — scheduling trajectory on arylcyclohexylamines is active. Very high emergency-scheduling risk.",
    benefitScore: 72,
    harmScore: 40,
    evidenceConfidence: "MODERATE",
    status: "Hold — Active Scheduling Risk",
    statusColor: "orange",
    goDecision: "HOLD — monitor DEA announcements; do not commit capacity",
    summary: "NMDA receptor antagonist with somewhat higher potency than ketamine. Also binds sigma-1/sigma-2 receptors, which may contribute to rapid antidepressant effects. Longer duration than ketamine (4–6 h vs 1–2 h for dissociative effects). Chloro substituent removed from ketamine scaffold — distinct metabolism.",
    pros: [
      "NMDA antagonism + sigma receptor activity → novel antidepressant research tool",
      "Longer duration than ketamine — different therapeutic session formats",
      "Distinct metabolism from ketamine due to missing chloro",
      "Unscheduled window for research-grade standard production"
    ],
    cons: [
      "Very high emergency-scheduling risk (2-FDCK precedent)",
      "Bladder toxicity is class concern (arylcyclohexylamine)",
      "Dependence potential",
      "Longer duration = higher dissociation burden"
    ],
    pharmacology: {
      targets: [
        { receptor: "NMDA (GluN2B)", activity: "Non-competitive antagonist", value: "Ki ≈ 0.3–0.6 μM" },
        { receptor: "σ1", activity: "Agonist", value: "Moderate affinity" },
        { receptor: "σ2", activity: "Agonist", value: "Moderate affinity" }
      ],
      duration: "4–6 h",
      mechanism: "NMDA antagonism triggers glutamate surge → AMPA receptor activation → BDNF/mTOR-dependent rapid antidepressant"
    },
    labSynthesis: {
      title: "Laboratory-Scale Synthesis",
      overview: "Modification of the ketamine synthetic route, omitting chlorination. Starting from bromocyclopentane + phenyl Grignard → α-aminoketone intermediate → cyclization.",
      steps: [
        { step: "1", title: "Phenyl cyclopentyl ketone formation", description: "Phenyl Grignard + bromocyclopentane/cyclopentanone → 1-phenyl-cyclopentanol.", solvent: "THF", keyReagents: "PhMgBr" },
        { step: "2", title: "Bromination", description: "NBS bromination at alpha-position.", solvent: "CCl₄ (replace with EtOAc for Wohler)", keyReagents: "NBS" },
        { step: "3", title: "Methylamine substitution", description: "Methylamine displacement of bromide → α-methylamino ketone.", solvent: "Ethanol", keyReagents: "Methylamine" },
        { step: "4", title: "Ring expansion (retro-aldol/aldol)", description: "Thermal ring-expansion to cyclohexanone.", solvent: "Decalin", keyReagents: "Thermal" },
        { step: "5", title: "HCl salt", description: "HCl in EtOAc.", solvent: "Ethyl Acetate", keyReagents: "HCl" }
      ],
      yield: "30–45 % over 5 steps",
      purityTarget: "≥ 99 %"
    },
    kgScaleSynthesis: {
      title: "Kilogram-Scale Plan",
      feasibility: "Moderate — arylcyclohexylamine synthesis is well-established but thermal ring-expansion step requires pressure-rated equipment.",
      solventsUsed: ["THF", "Ethyl Acetate", "Ethanol", "Heptane"],
      equipment: ["Grignard-rated reactor", "Pressure reactor for ring expansion", "Standard crystallizer"],
      batchPlan: {
        targetBatch: "1 kg DCK·HCl",
        startingMaterial: "2.5 kg phenylmagnesium bromide solution",
        keyVolumes: ["THF: 20 L", "EtOAc: 15 L", "EtOH: 10 L"]
      },
      hazards: ["Grignard + pyrophoric; NBS halogenation; methylamine gas"],
      vendorNotes: ["Sigma, Fisher for reagents"],
      estCostPerKg: "$22,000 – $32,000"
    },
    safety: {
      acuteToxicity: "Limited formal tox data; human use data suggests ketamine-range risk profile",
      chronicConcerns: ["Bladder toxicity (ulcerative cystitis) — class effect", "Dependence", "Cognitive impairment with chronic use"],
      contraindications: ["Severe hypertension", "Psychosis spectrum", "Hepatic impairment"],
      acuteSideEffects: ["Dissociation", "Analgesia", "Amnesia", "Transient HTN and tachycardia", "Nausea"],
      ehsClassification: "GHS Cat. 4 oral; CNS-active — full industrial hygiene"
    },
    publishedResearch: [
      { citation: "Wallach J, Brandt SD. Phencyclidine-Based New Psychoactive Substances. Handb Exp Pharmacol. 2018;252:261-303." }
    ],
    patentInfo: "Composition-of-matter patents expired; method and formulation space available.",
    wohlerScoring: {
      methodology: "Wohler Scoring System v1.0",
      benefitBreakdown: { therapeuticRationale: 18, evidenceQuality: 14, unmetMedicalNeed: 18, novelty: 10, universityInterest: 12 },
      harmBreakdown: { acuteToxicity: 8, chronicToxicity: 12, dependence: 10, schedulingRisk: 10 },
      netValue: 32,
      confidenceRationale: "MODERATE — pharmacology understood; scheduling risk dominant negative."
    },
    priorityTier: "Tier 3 — Hold on scheduling monitor",
    nextSteps: ["Monitor DEA Federal Register for arylcyclohexylamine scheduling", "Hold commit to production"]
  },

  "2-fdck": {
    id: "2-fdck",
    name: "2-FDCK",
    fullName: "2-Fluorodeschloroketamine",
    aliases: ["2F-DCK", "F-Ketamine"],
    categoryId: "dissociatives",
    program: "Program F",
    cas: "111982-49-1",
    formula: "C₁₃H₁₆FNO",
    molecularWeight: "221.27 g/mol",
    schedulingStatus: "scheduled",
    schedulingNote: "⚠️ FEDERALLY SCHEDULED — temporarily Schedule I as of January 2026. No longer a research-use-only unscheduled compound. Included here for historical reference and as a cautionary data point. Any work requires DEA Schedule I registration and full institutional controls.",
    benefitScore: 60,
    harmScore: 78,
    evidenceConfidence: "HIGH",
    status: "Scheduled — Do Not Produce",
    statusColor: "red",
    goDecision: "KILL — federally scheduled 2026",
    summary: "Fluorinated ketamine analog that was emergency-scheduled by DEA in January 2026. Analytical reference standard (Cayman Chemical CRM certificate available, 99.42 % purity by HPLC). Included in the Wohler database as a scheduling cautionary reference and to document the regulatory trajectory for related arylcyclohexylamines.",
    pros: [
      "Clean analytical data available for reference (Cayman Chemical CRM)",
      "Demonstrates scheduling trajectory — important regulatory intelligence"
    ],
    cons: [
      "Federally scheduled — research requires Schedule I DEA registration",
      "Bladder toxicity reports in human use literature"
    ],
    pharmacology: {
      targets: [
        { receptor: "NMDA", activity: "Non-competitive antagonist", value: "Ki similar to DCK" }
      ],
      duration: "2–4 h",
      mechanism: "NMDA antagonism — identical mechanism class"
    },
    labSynthesis: { title: "N/A — Scheduled", overview: "Do not produce without DEA Schedule I registration.", steps: [], yield: "—", purityTarget: "—" },
    kgScaleSynthesis: { title: "N/A", feasibility: "N/A — Scheduled", solventsUsed: [], equipment: [], batchPlan: {}, hazards: [], vendorNotes: [], estCostPerKg: "—" },
    safety: {
      acuteToxicity: "Multiple recreational-use fatalities reported prior to scheduling",
      chronicConcerns: ["Bladder toxicity", "Dependence", "Cognitive impairment"],
      contraindications: ["Use only under DEA Schedule I research"],
      acuteSideEffects: ["Dissociation", "Amnesia", "CV effects"],
      ehsClassification: "Schedule I controls"
    },
    publishedResearch: [
      { citation: "Cayman Chemical. 2-Fluorodeschloroketamine CRM Certificate of Analysis. 2023. (99.42 % purity by HPLC)" },
      { citation: "DEA Diversion Control Division. Temporary Placement of 2-FDCK into Schedule I. Federal Register. January 2026." }
    ],
    patentInfo: "N/A — Scheduled.",
    wohlerScoring: {
      methodology: "Wohler Scoring System v1.0",
      benefitBreakdown: { therapeuticRationale: 12, evidenceQuality: 18, unmetMedicalNeed: 12, novelty: 8, universityInterest: 10 },
      harmBreakdown: { acuteToxicity: 18, chronicToxicity: 15, dependence: 15, schedulingRisk: 30 },
      netValue: -18,
      confidenceRationale: "HIGH — fully scheduled; cautionary reference only."
    },
    priorityTier: "KILL — Scheduled",
    nextSteps: ["Remove from active production consideration", "Retain analytical standard for comparison/QC purposes only"]
  },

  "fluorexetamine": {
    id: "fluorexetamine",
    name: "Fluorexetamine (FXE)",
    fullName: "3-Fluoromethoxetamine",
    aliases: ["FXE", "3F-MXE"],
    categoryId: "dissociatives",
    program: "Program F",
    formula: "C₁₅H₂₀FNO₂",
    molecularWeight: "265.33 g/mol",
    schedulingStatus: "unscheduled",
    schedulingNote: "Not federally scheduled as of 2026. MXE (parent) was never federally scheduled in U.S. Given 2-FDCK precedent, monitoring is essential.",
    benefitScore: 65,
    harmScore: 42,
    evidenceConfidence: "MODERATE",
    status: "Hold — Active Scheduling Risk",
    statusColor: "orange",
    goDecision: "HOLD — monitor scheduling; consider non-arylcyclohexylamine glutamate tools instead",
    summary: "Fluorinated methoxetamine (MXE) analog. NMDA antagonist with improved metabolic stability and BBB penetration vs. MXE due to fluorine. Could serve as a research tool for NMDA antagonist antidepressant mechanism with more predictable PK than MXE.",
    pros: ["Fluorine-enhanced PK vs MXE", "Clear NMDA antagonist pharmacology", "Unscheduled window"],
    cons: ["Arylcyclohexylamine scheduling trajectory", "Bladder toxicity class effect", "Limited formal pharmacology data"],
    pharmacology: {
      targets: [
        { receptor: "NMDA", activity: "Non-competitive antagonist", value: "Similar to MXE" }
      ],
      duration: "2–4 h",
      mechanism: "NMDA antagonism — glutamate surge antidepressant hypothesis"
    },
    labSynthesis: {
      title: "Laboratory-Scale Synthesis",
      overview: "Similar to MXE route with 3-fluorophenyl starting material.",
      steps: [
        { step: "1", title: "3-Fluoro-phenyl Grignard formation", description: "From 3-fluorobromobenzene + Mg in THF.", solvent: "THF", keyReagents: "3-F-C₆H₄Br, Mg" },
        { step: "2", title: "Addition to cyclohexanone", description: "Grignard addition to 2-methoxy-cyclohexanone.", solvent: "THF", keyReagents: "2-methoxy-cyclohexanone" },
        { step: "3", title: "Aminomethylation / ring closure", description: "Analogous to MXE synthesis.", solvent: "EtOAc", keyReagents: "Ethylamine" },
        { step: "4", title: "HCl salt", description: "HCl/EtOAc crystallization.", solvent: "EtOAc", keyReagents: "HCl" }
      ],
      yield: "~35 %",
      purityTarget: "≥ 99 %"
    },
    kgScaleSynthesis: {
      title: "Kilogram-Scale Plan",
      feasibility: "Moderate — same as DCK class",
      solventsUsed: ["THF", "Ethyl Acetate", "Ethanol", "Heptane"],
      equipment: ["Grignard reactor", "Standard crystallizer"],
      batchPlan: { targetBatch: "1 kg FXE·HCl", startingMaterial: "3 kg 3-fluorobromobenzene", keyVolumes: ["THF: 25 L", "EtOAc: 15 L"] },
      hazards: ["Grignard; fluorinated aryl halide; ethylamine"],
      vendorNotes: ["3-Fluorobromobenzene: Sigma, Combi-Blocks, Matrix"],
      estCostPerKg: "$28,000 – $40,000"
    },
    safety: {
      acuteToxicity: "Limited; assume MXE-range",
      chronicConcerns: ["Bladder toxicity", "Dependence"],
      contraindications: ["CV disease", "Hepatic impairment", "Psychosis"],
      acuteSideEffects: ["Dissociation", "Analgesia", "Amnesia"],
      ehsClassification: "GHS Cat. 4 oral"
    },
    publishedResearch: [
      { citation: "Wallach J, Brandt SD. Phencyclidine-Based New Psychoactive Substances. Handb Exp Pharmacol. 2018;252:261-303." }
    ],
    patentInfo: "Method patents possible.",
    wohlerScoring: {
      methodology: "Wohler Scoring System v1.0",
      benefitBreakdown: { therapeuticRationale: 16, evidenceQuality: 12, unmetMedicalNeed: 15, novelty: 12, universityInterest: 10 },
      harmBreakdown: { acuteToxicity: 8, chronicToxicity: 12, dependence: 10, schedulingRisk: 12 },
      netValue: 23,
      confidenceRationale: "MODERATE — limited data; scheduling risk dominates."
    },
    priorityTier: "Tier 3 — Hold",
    nextSteps: ["Monitor scheduling", "Consider non-arylcyclohexylamine glutamate tools (ketamine metabolites, sigma modulators) as better-priority alternatives"]
  }
};